// src/index.ts
import * as Docx from 'docx';

// src/export-docx.ts
import { Packer } from 'docx';

// src/convert.ts
import { Document, LevelFormat, Paragraph as Paragraph6, Table as Table3 } from 'docx';

// src/constants.ts
import { AlignmentType } from 'docx';

// src/utils/line-height-to-docx.ts
function lineHeightToDocx(lineHeight) {
  return lineHeight * 240;
}

// src/utils/points-to-half-points.ts
function pointsToHalfPoints(points) {
  return points * 2;
}

// src/utils/points-to-twips.ts
function pointsToTwips(points) {
  return points * 20;
}

// src/constants.ts
var defaultStyles = {
  paragraphStyles: [
    // Normal style (default for most paragraphs)
    {
      id: 'Normal',
      name: 'Normal',
      run: {
        font: 'Aptos',
        size: pointsToHalfPoints(11),
      },
      paragraph: {
        spacing: {
          before: 0,
          after: pointsToTwips(10),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // List Paragraph style (used for bullets and numbering)
    {
      id: 'ListParagraph',
      name: 'List Paragraph',
      basedOn: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos',
        size: pointsToHalfPoints(11),
      },
      paragraph: {
        spacing: {
          before: 0,
          after: pointsToTwips(2),
          line: lineHeightToDocx(1),
        },
      },
    },
    // Heading 1 style
    {
      id: 'Heading1',
      name: 'Heading 1',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos Light',
        size: pointsToHalfPoints(20),
        bold: true,
        color: '000000',
      },
      paragraph: {
        spacing: {
          before: pointsToTwips(12),
          after: pointsToTwips(6),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Heading 2 style
    {
      id: 'Heading2',
      name: 'Heading 2',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos Light',
        size: pointsToHalfPoints(16),
        bold: true,
        color: '0F172A',
      },
      paragraph: {
        spacing: {
          before: pointsToTwips(12),
          after: pointsToTwips(6),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Heading 3 style
    {
      id: 'Heading3',
      name: 'Heading 3',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos',
        size: pointsToHalfPoints(14),
        bold: true,
        color: '1E293B',
      },
      paragraph: {
        spacing: {
          before: pointsToTwips(12),
          after: pointsToTwips(6),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Heading 4 style
    {
      id: 'Heading4',
      name: 'Heading 4',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos',
        size: pointsToHalfPoints(12),
        bold: true,
        color: '1E293B',
      },
      paragraph: {
        spacing: {
          before: pointsToTwips(12),
          after: pointsToTwips(6),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Heading 5 style
    {
      id: 'Heading5',
      name: 'Heading 5',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos',
        size: pointsToHalfPoints(11),
        bold: true,
        color: '334155',
      },
      paragraph: {
        spacing: {
          before: pointsToTwips(12),
          after: pointsToTwips(6),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Heading 6 style
    {
      id: 'Heading6',
      name: 'Heading 6',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos',
        size: pointsToHalfPoints(11),
        bold: true,
        color: '475569',
      },
      paragraph: {
        spacing: {
          before: pointsToTwips(12),
          after: pointsToTwips(6),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Title style
    {
      id: 'Title',
      name: 'Title',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos Light',
        size: pointsToHalfPoints(22),
        bold: true,
        color: '000000',
      },
      paragraph: {
        alignment: AlignmentType.CENTER,
        spacing: {
          before: 0,
          after: 0,
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Subtitle style
    {
      id: 'Subtitle',
      name: 'Subtitle',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos Light',
        size: pointsToHalfPoints(16),
        italics: true,
        color: '666666',
      },
      paragraph: {
        alignment: AlignmentType.CENTER,
        spacing: {
          before: 0,
          after: 0,
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Quote style (typically for indented, italic text)
    {
      id: 'Quote',
      name: 'Quote',
      basedOn: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos',
        italics: true,
      },
      paragraph: {
        alignment: AlignmentType.CENTER,
        spacing: {
          before: pointsToTwips(10),
          after: pointsToTwips(10),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // Intense Quote style (more pronounced indentation)
    {
      id: 'IntenseQuote',
      name: 'Intense Quote',
      basedOn: 'Normal',
      quickFormat: true,
      run: {
        font: 'Aptos',
        italics: true,
        color: '444444',
      },
      paragraph: {
        alignment: AlignmentType.CENTER,
        spacing: {
          before: pointsToTwips(10),
          after: pointsToTwips(10),
          line: lineHeightToDocx(1.15),
        },
      },
    },
    // No Spacing style (no extra space before or after paragraphs)
    {
      id: 'NoSpacing',
      name: 'No Spacing',
      basedOn: 'Normal',
      quickFormat: true,
      paragraph: {
        spacing: {
          before: 0,
          after: 0,
          line: lineHeightToDocx(1),
        },
      },
    },
    // Hyperlink style
    {
      id: 'Hyperlink',
      name: 'Hyperlink',
      basedOn: 'Normal',
      run: {
        color: '0563C1',
        underline: {
          type: 'single',
        },
      },
    },
  ],
};
var DEFAULT_IMAGE_DIMENSIONS = {
  width: 800,
  height: 400,
};

// src/services/paragraph/convert-paragraph.ts
import {
  AlignmentType as AlignmentType2,
  HeadingLevel,
  Paragraph as Paragraph2,
  TextRun as TextRun2,
} from 'docx';

// src/services/image/convert-image.ts
import { ImageRun, Paragraph } from 'docx';

// src/utils/get-dimensions.client.ts
async function getClientDimensions(buffer) {
  const sample = new TextDecoder('utf-8').decode(buffer.subarray(0, 100));
  let mimeType = 'image/png';
  if (sample.includes('<svg')) {
    mimeType = 'image/svg+xml';
  } else if (sample.startsWith('\x89PNG')) {
    mimeType = 'image/png';
  } else if (sample.startsWith('\xFF\xD8')) {
    mimeType = 'image/jpeg';
  }
  return new Promise((resolve, reject) => {
    const blob = new Blob([buffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

// src/utils/get-dimensions.server.ts
async function getServerDimensions(buffer) {
  try {
    const { imageSize } = await import('./image-size.js');
    const dimensions = imageSize(buffer);
    if (dimensions.width && dimensions.height) {
      return { width: dimensions.width, height: dimensions.height };
    }
  } catch (error) {
    console.warn('Error getting server-side image dimensions:', error);
  }
  return DEFAULT_IMAGE_DIMENSIONS;
}

// src/services/image/convert-image.ts
var ALLOWED_TYPES = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'];
function detectFileType(src, responseHeaders) {
  const contentType = responseHeaders.get('content-type');
  if (contentType) {
    if (contentType.includes('png')) {
      return 'png';
    }
    if (contentType.includes('jpeg') || contentType.includes('jpg')) {
      return 'jpg';
    }
    if (contentType.includes('gif')) {
      return 'gif';
    }
    if (contentType.includes('bmp')) {
      return 'bmp';
    }
    if (contentType.includes('svg')) {
      return 'svg';
    }
  }
  if (contentType === 'binary/octet-stream') {
    const url = new URL(src);
    const fileName = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
    const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    if (ALLOWED_TYPES.includes(fileExtension)) {
      return fileExtension;
    }
  }
  throw new Error(
    `Unsupported image type [${contentType}]. Only ${ALLOWED_TYPES.join(', ')} are supported.`
  );
}
function pixelsToPoints(pixels) {
  return pixels * 0.75;
}
async function getIntrinsicDimensions(buffer) {
  try {
    if (typeof window !== 'undefined') {
      return await getClientDimensions(buffer);
    }
    return await getServerDimensions(buffer);
  } catch (error) {
    console.warn('Could not determine image dimensions, using defaults:', error);
    return { width: 800, height: 400 };
  }
}
async function convertImage({ node, imageOverrides }) {
  var _a, _b, _c, _d;
  const src = (_a = node.attrs) == null ? void 0 : _a.src;
  if (!src) {
    throw new Error('Image node missing src attribute.');
  }
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(`Failed to fetch image from ${src}: ${response.statusText}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  const buffer =
    typeof Buffer !== 'undefined' && typeof Buffer.from === 'function'
      ? Buffer.from(arrayBuffer)
      : new Uint8Array(arrayBuffer);
  const fileType = detectFileType(src, response.headers);
  let intrinsicDimensions = { width: 800, height: 400 };
  try {
    intrinsicDimensions = await getIntrinsicDimensions(buffer);
  } catch (error) {
    console.warn('Could not determine intrinsic dimensions, using defaults.', error);
  }
  const rawWidth =
    ((_b = node.attrs) == null ? void 0 : _b.width) != null ? Number(node.attrs.width) : null;
  const rawHeight =
    ((_c = node.attrs) == null ? void 0 : _c.height) != null ? Number(node.attrs.height) : null;
  const attrWidth = rawWidth !== null && !Number.isNaN(rawWidth) && rawWidth > 0 ? rawWidth : null;
  const attrHeight =
    rawHeight !== null && !Number.isNaN(rawHeight) && rawHeight > 0 ? rawHeight : null;
  let width;
  let height;
  if (attrWidth !== null && attrHeight !== null) {
    width = pixelsToPoints(attrWidth);
    height = pixelsToPoints(attrHeight);
  } else if (attrWidth !== null) {
    const aspectRatio = intrinsicDimensions.height / intrinsicDimensions.width;
    width = pixelsToPoints(attrWidth);
    height = width * aspectRatio;
  } else if (attrHeight !== null) {
    const aspectRatio = intrinsicDimensions.width / intrinsicDimensions.height;
    height = pixelsToPoints(attrHeight);
    width = height * aspectRatio;
  } else {
    width = pixelsToPoints(intrinsicDimensions.width);
    height = pixelsToPoints(intrinsicDimensions.height);
  }
  const altText = ((_d = node.attrs) == null ? void 0 : _d.alt)
    ? { name: node.attrs.alt, description: node.attrs.alt }
    : void 0;
  let imageOptions;
  if (fileType === 'svg') {
    imageOptions = {
      data: buffer,
      transformation: { width, height },
      altText,
      type: fileType,
      ...imageOverrides,
      fallback: { type: 'jpg', data: '' },
      // You can adjust fallback as needed.
    };
  } else {
    imageOptions = {
      data: buffer,
      transformation: { width, height },
      altText,
      type: fileType,
      ...imageOverrides,
    };
  }
  const imageRun = new ImageRun(imageOptions);
  return new Paragraph({ children: [imageRun] });
}

// src/services/math/convert-inline-math.ts
import { Math as DocxMath, MathRun as MathRun2 } from 'docx';

// src/services/math/parse-latex-to-math-components.ts
import {
  MathFraction,
  MathRadical,
  MathRun,
  MathSubScript,
  MathSubSuperScript,
  MathSuperScript,
} from 'docx';

// src/creators/math/create-math-matrix-element.ts
import { BuilderElement as BuilderElement4 } from 'docx';

// src/creators/math/create-math-matrix-properties.ts
import { BuilderElement } from 'docx';
var createMathMatrixProperties = (options) => {
  const { columnCount, columnAlignment = 'center' } = options;
  const matrixColumnProperties = new BuilderElement({
    name: 'm:mcPr',
    children: [
      new BuilderElement({
        name: 'm:count',
        attributes: {
          val: {
            key: 'm:val',
            value: columnCount.toString(),
          },
        },
      }),
      new BuilderElement({
        name: 'm:mcJc',
        attributes: {
          val: {
            key: 'm:val',
            value: columnAlignment,
          },
        },
      }),
    ],
  });
  const matrixColumn = new BuilderElement({
    name: 'm:mc',
    children: [matrixColumnProperties],
  });
  const matrixColumnSettings = new BuilderElement({
    name: 'm:mcs',
    children: [matrixColumn],
  });
  const runProperties = new BuilderElement({
    name: 'w:rPr',
    children: [
      new BuilderElement({
        name: 'w:rFonts',
        attributes: {
          ascii: {
            key: 'w:ascii',
            value: 'Cambria Math',
          },
          hAnsi: {
            key: 'w:hAnsi',
            value: 'Cambria Math',
          },
        },
      }),
      new BuilderElement({
        name: 'w:i',
      }),
    ],
  });
  const controlProperties = new BuilderElement({
    name: 'm:ctrlPr',
    children: [runProperties],
  });
  return new BuilderElement({
    name: 'm:mPr',
    children: [matrixColumnSettings, controlProperties],
  });
};

// src/creators/math/create-math-matrix-row.ts
import { BuilderElement as BuilderElement3 } from 'docx';

// src/creators/math/create-math-matrix-cell.ts
import { BuilderElement as BuilderElement2 } from 'docx';
var createMathMatrixCell = (mathContent) => {
  return new BuilderElement2({
    name: 'm:e',
    children: mathContent,
  });
};

// src/creators/math/create-math-matrix-row.ts
var createMathMatrixRow = (rowData) => {
  const cellElements = rowData.cells.map((cellContent) => createMathMatrixCell(cellContent));
  return new BuilderElement3({
    name: 'm:mr',
    children: cellElements,
  });
};

// src/creators/math/create-math-matrix-element.ts
var createMathMatrixElement = (options) => {
  const { rows, columnCount, columnAlignment } = options;
  const matrixProperties = createMathMatrixProperties({
    columnCount,
    columnAlignment,
  });
  const matrixRows = rows.map((rowData) => createMathMatrixRow(rowData));
  return new BuilderElement4({
    name: 'm:m',
    children: [matrixProperties, ...matrixRows],
  });
};

// src/services/math/math-map.ts
var latexToUnicodeMap = {
  // Trigonometric functions
  '\\sin': 'sin',
  '\\cos': 'cos',
  '\\tan': 'tan',
  '\\sec': 'sec',
  '\\csc': 'csc',
  '\\cot': 'cot',
  // Hyperbolic functions
  '\\sinh': 'sinh',
  '\\cosh': 'cosh',
  '\\tanh': 'tanh',
  '\\sech': 'sech',
  '\\csch': 'csch',
  '\\coth': 'coth',
  // Inverse trigonometric functions
  '\\arcsin': 'arcsin',
  '\\arccos': 'arccos',
  '\\arctan': 'arctan',
  '\\arcsec': 'arcsec',
  '\\arccsc': 'arccsc',
  '\\arccot': 'arccot',
  // Logarithmic functions
  '\\log': 'log',
  '\\ln': 'ln',
  // Mathematical operators and summation symbols
  '\\sum': '\u2211',
  '\\int': '\u222B',
  '\\oint': '\u222E',
  '\\iint': '\u222C',
  '\\iiint': '\u222D',
  '\\prod': '\u220F',
  '\\coprod': '\u2210',
  '\\lim': 'lim',
  '\\limsup': 'lim sup',
  '\\liminf': 'lim inf',
  '\\max': 'max',
  '\\min': 'min',
  '\\sup': 'sup',
  '\\inf': 'inf',
  '\\arg': 'arg',
  '\\ker': 'ker',
  '\\dim': 'dim',
  '\\hom': 'hom',
  '\\det': 'det',
  '\\exp': 'exp',
  '\\deg': 'deg',
  '\\gcd': 'gcd',
  '\\lcm': 'lcm',
  // Greek letters (lowercase)
  '\\alpha': '\u03B1',
  '\\beta': '\u03B2',
  '\\gamma': '\u03B3',
  '\\delta': '\u03B4',
  '\\epsilon': '\u03B5',
  '\\varepsilon': '\u03B5',
  '\\zeta': '\u03B6',
  '\\eta': '\u03B7',
  '\\theta': '\u03B8',
  '\\vartheta': '\u03D1',
  '\\iota': '\u03B9',
  '\\kappa': '\u03BA',
  '\\lambda': '\u03BB',
  '\\mu': '\u03BC',
  '\\nu': '\u03BD',
  '\\xi': '\u03BE',
  '\\omicron': '\u03BF',
  '\\pi': '\u03C0',
  '\\varpi': '\u03D6',
  '\\rho': '\u03C1',
  '\\varrho': '\u03F1',
  '\\sigma': '\u03C3',
  '\\varsigma': '\u03C2',
  '\\tau': '\u03C4',
  '\\upsilon': '\u03C5',
  '\\phi': '\u03C6',
  '\\varphi': '\u03C6',
  '\\chi': '\u03C7',
  '\\psi': '\u03C8',
  '\\omega': '\u03C9',
  // Greek letters (uppercase)
  '\\Alpha': '\u0391',
  '\\Beta': '\u0392',
  '\\Gamma': '\u0393',
  '\\Delta': '\u0394',
  '\\Epsilon': '\u0395',
  '\\Zeta': '\u0396',
  '\\Eta': '\u0397',
  '\\Theta': '\u0398',
  '\\Iota': '\u0399',
  '\\Kappa': '\u039A',
  '\\Lambda': '\u039B',
  '\\Mu': '\u039C',
  '\\Nu': '\u039D',
  '\\Xi': '\u039E',
  '\\Omicron': '\u039F',
  '\\Pi': '\u03A0',
  '\\Rho': '\u03A1',
  '\\Sigma': '\u03A3',
  '\\Tau': '\u03A4',
  '\\Upsilon': '\u03A5',
  '\\Phi': '\u03A6',
  '\\Chi': '\u03A7',
  '\\Psi': '\u03A8',
  '\\Omega': '\u03A9',
  // Mathematical symbols
  '\\infty': '\u221E',
  '\\pm': '\xB1',
  '\\mp': '\u2213',
  '\\cdot': '\u22C5',
  '\\times': '\xD7',
  '\\div': '\xF7',
  '\\setminus': '\u2216',
  '\\backslash': '\u2216',
  '\\partial': '\u2202',
  '\\nabla': '\u2207',
  '\\triangle': '\u25B3',
  '\\square': '\u25A1',
  '\\blacksquare': '\u25A0',
  '\\diamond': '\u25CA',
  '\\blackdiamond': '\u25C6',
  '\\emptyset': '\u2205',
  '\\varnothing': '\u2205',
  // Comparison operators
  '\\leq': '\u2264',
  '\\le': '\u2264',
  '\\geq': '\u2265',
  '\\ge': '\u2265',
  '\\neq': '\u2260',
  '\\ne': '\u2260',
  '\\ll': '\u226A',
  '\\gg': '\u226B',
  '\\lll': '\u22D8',
  '\\ggg': '\u22D9',
  '\\approx': '\u2248',
  '\\simeq': '\u2243',
  '\\cong': '\u2245',
  '\\equiv': '\u2261',
  '\\sim': '\u223C',
  '\\propto': '\u221D',
  '\\prec': '\u227A',
  '\\succ': '\u227B',
  '\\preceq': '\u2AAF',
  '\\succeq': '\u2AB0',
  '\\parallel': '\u2225',
  '\\perp': '\u22A5',
  '\\mid': '\u2223',
  '\\nmid': '\u2224',
  // Set theory symbols
  '\\sub': '\u2282',
  '\\cup': '\u222A',
  '\\cap': '\u2229',
  '\\subset': '\u2282',
  '\\supset': '\u2283',
  '\\subseteq': '\u2286',
  '\\supseteq': '\u2287',
  '\\subsetneq': '\u228A',
  '\\supsetneq': '\u228B',
  '\\in': '\u2208',
  '\\notin': '\u2209',
  '\\ni': '\u220B',
  '\\owns': '\u220B',
  '\\notni': '\u220C',
  '\\sqsubset': '\u228F',
  '\\sqsupset': '\u2290',
  '\\sqsubseteq': '\u2291',
  '\\sqsupseteq': '\u2292',
  '\\bigcup': '\u22C3',
  '\\bigcap': '\u22C2',
  '\\bigsqcup': '\u2294',
  // Logic symbols
  '\\exists': '\u2203',
  '\\forall': '\u2200',
  '\\neg': '\xAC',
  '\\land': '\u2227',
  '\\lor': '\u2228',
  '\\lnot': '\xAC',
  '\\top': '\u22A4',
  '\\bot': '\u22A5',
  '\\vdash': '\u22A2',
  '\\models': '\u22A8',
  // Arrows and implications
  '\\leftarrow': '\u2190',
  '\\gets': '\u2190',
  '\\rightarrow': '\u2192',
  '\\to': '\u2192',
  '\\leftrightarrow': '\u2194',
  '\\uparrow': '\u2191',
  '\\downarrow': '\u2193',
  '\\updownarrow': '\u2195',
  '\\Leftarrow': '\u21D0',
  '\\Rightarrow': '\u21D2',
  '\\implies': '\u21D2',
  '\\Leftrightarrow': '\u21D4',
  '\\iff': '\u21D4',
  '\\Uparrow': '\u21D1',
  '\\Downarrow': '\u21D3',
  '\\Updownarrow': '\u21D5',
  '\\mapsto': '\u21A6',
  '\\longmapsto': '\u27FC',
  '\\hookleftarrow': '\u21A9',
  '\\hookrightarrow': '\u21AA',
  '\\leftharpoonup': '\u21BC',
  '\\rightharpoonup': '\u21C0',
  '\\leftharpoondown': '\u21BD',
  '\\rightharpoondown': '\u21C1',
  '\\rightleftharpoons': '\u21CC',
  '\\overleftarrow': '\u2190',
  '\\overrightarrow': '\u2192',
  '\\overleftrightarrow': '\u2194',
  '\\from': '\u2190',
  // Other mathematical symbols
  '\\ast': '\u2217',
  '\\star': '\u22C6',
  '\\circ': '\u2218',
  '\\bullet': '\u2022',
  // Accents and decorations
  '\\dot': '\u02D9',
  '\\ddot': '\xA8',
  '\\tilde': '~',
  '\\bar': '\xAF',
  '\\hat': '^',
  '\\check': '\u02C7',
  '\\acute': '\xB4',
  '\\grave': '`',
  '\\overline': '\xAF',
  '\\underline': '\xAF',
  '\\overbrace': '\u23DE',
  '\\underbrace': '\u23DF',
  // Square root symbol
  '\\sqrt': '\u221A',
  // Number sets (blackboard bold)
  '\\mathbb{N}': '\u2115',
  '\\mathbb{Z}': '\u2124',
  '\\mathbb{Q}': '\u211A',
  '\\mathbb{R}': '\u211D',
  '\\mathbb{C}': '\u2102',
  '\\mathbb{H}': '\u210D',
  '\\mathbb{P}': '\u2119',
  '\\mathbb{E}': 'E',
  // Floor and ceiling functions
  '\\lfloor': '\u230A',
  '\\rfloor': '\u230B',
  '\\lceil': '\u2308',
  '\\rceil': '\u2309',
  // Brackets and delimiters
  '\\langle': '\u27E8',
  '\\rangle': '\u27E9',
  '\\lbrace': '{',
  '\\rbrace': '}',
  '\\lvert': '|',
  '\\rvert': '|',
  '\\lVert': '\u2016',
  '\\rVert': '\u2016',
  // Miscellaneous symbols
  '\\angle': '\u2220',
  '\\measuredangle': '\u2221',
  '\\sphericalangle': '\u2222',
  '\\therefore': '\u2234',
  '\\because': '\u2235',
  '\\QED': '\u220E',
  '\\boxtimes': '\u22A0',
  '\\boxplus': '\u229E',
  '\\boxminus': '\u229F',
  '\\boxdot': '\u22A1',
  // Modular arithmetic
  '\\bmod': 'mod',
  '\\pmod': 'mod',
  '\\mod': 'mod',
  // Special functions
  '\\Re': '\u211C',
  '\\Im': '\u2111',
  '\\wp': '\u2118',
  '\\ell': '\u2113',
  '\\hbar': '\u210F',
  '\\mho': '\u2127',
  '\\Finv': '\u2132',
  '\\Game': '\u2141',
  '\\Bbbk': '\u{1D55C}',
  // Diacritical marks (combining)
  '\\vec': '\u20D7',
  '\\mathring': '\u02DA',
};

// src/services/math/parse-latex-to-math-components.ts
function parseLatexToMathComponents(latex) {
  const components = [];
  let remaining = latex.trim().replace(/\\\\/g, '\\');
  while (remaining.length > 0) {
    let processed = false;
    let match = remaining.match(/^\\sqrt\[([^\]]*)\]\{([^}]*)\}/);
    if (match) {
      const degree = match[1];
      const content = match[2];
      const degreeComponents = parseLatexToMathComponents(degree);
      const innerComponents = parseLatexToMathComponents(content);
      components.push(
        new MathRadical({
          children: innerComponents,
          degree: degreeComponents,
        })
      );
      remaining = remaining.slice(match[0].length);
      processed = true;
    }
    if (!processed) {
      match = remaining.match(/^\\sqrt\{([^}]*)\}/);
      if (match) {
        const content = match[1];
        const innerComponents = parseLatexToMathComponents(content);
        components.push(
          new MathRadical({
            children: innerComponents,
          })
        );
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\\frac\{([^}]*)\}\{([^}]*)\}/);
      if (match) {
        const numerator = parseLatexToMathComponents(match[1]);
        const denominator = parseLatexToMathComponents(match[2]);
        components.push(
          new MathFraction({
            numerator,
            denominator,
          })
        );
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\\binom\{([^}]*)\}\{([^}]*)\}/);
      if (match) {
        const n = parseLatexToMathComponents(match[1]);
        const k = parseLatexToMathComponents(match[2]);
        components.push(new MathRun('('));
        components.push(
          new MathFraction({
            numerator: n,
            denominator: k,
          })
        );
        components.push(new MathRun(')'));
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      for (const [latexCmd, unicode] of Object.entries(latexToUnicodeMap)) {
        if (remaining.startsWith(latexCmd)) {
          components.push(new MathRun(unicode));
          remaining = remaining.slice(latexCmd.length);
          processed = true;
          break;
        }
      }
    }
    if (!processed) {
      match =
        remaining.match(/^_(\{[^}]*\}|.)\^(\{[^}]*\}|.)/) ||
        remaining.match(/^\^(\{[^}]*\}|.)_(\{[^}]*\}|.)/);
      if (match) {
        if (components.length === 0) {
          components.push(new MathRun(''));
        }
        const lastComponent = components.pop();
        let subContent;
        let supContent;
        if (remaining.startsWith('_')) {
          subContent = match[1].startsWith('{') ? match[1].slice(1, -1) : match[1];
          supContent = match[2].startsWith('{') ? match[2].slice(1, -1) : match[2];
        } else {
          supContent = match[1].startsWith('{') ? match[1].slice(1, -1) : match[1];
          subContent = match[2].startsWith('{') ? match[2].slice(1, -1) : match[2];
        }
        const subComponents = parseLatexToMathComponents(subContent);
        const supComponents = parseLatexToMathComponents(supContent);
        components.push(
          new MathSubSuperScript({
            children: [lastComponent],
            subScript: subComponents,
            superScript: supComponents,
          })
        );
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\^(\{[^}]*\}|.)/);
      if (match) {
        if (components.length === 0) {
          components.push(new MathRun(''));
        }
        const lastComponent = components.pop();
        const supContent = match[1].startsWith('{') ? match[1].slice(1, -1) : match[1];
        const supComponents = parseLatexToMathComponents(supContent);
        components.push(
          new MathSuperScript({
            children: [lastComponent],
            superScript: supComponents,
          })
        );
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^_(\{[^}]*\}|.)/);
      if (match) {
        if (components.length === 0) {
          components.push(new MathRun(''));
        }
        const lastComponent = components.pop();
        const subContent = match[1].startsWith('{') ? match[1].slice(1, -1) : match[1];
        const subComponents = parseLatexToMathComponents(subContent);
        components.push(
          new MathSubScript({
            children: [lastComponent],
            subScript: subComponents,
          })
        );
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\\(left|right)(.?)/);
      if (match) {
        if (match[2] && match[2] !== '\\' && match[2] !== '.') {
          components.push(new MathRun(match[2]));
        }
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\\begin\{matrix\}([\s\S]*?)\\end\{matrix\}/);
      if (match) {
        const matrixContent = match[1];
        const rows = matrixContent.split(/\\\\|\\(?=[0-9&])/).filter((row) => row.trim());
        const matrixRows = rows.map((row) => {
          const cells = row.split('&').map((cell) => cell.trim());
          return {
            cells: cells.map((cellContent) => {
              if (cellContent) {
                return parseLatexToMathComponents(cellContent);
              }
              return [new MathRun('')];
            }),
          };
        });
        const columnCount = matrixRows.length > 0 ? matrixRows[0].cells.length : 1;
        const matrixElement = createMathMatrixElement({
          rows: matrixRows,
          columnCount,
          columnAlignment: 'center',
        });
        components.push(matrixElement);
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\\begin\{[^}]*\}/);
      if (match) {
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\\end\{[^}]*\}/);
      if (match) {
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^\\text\{([^}]*)\}/);
      if (match) {
        components.push(new MathRun(match[1]));
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed && (remaining.startsWith('&') || remaining.startsWith('\\\\'))) {
      if (remaining.startsWith('&')) {
        components.push(new MathRun(' '));
        remaining = remaining.slice(1);
      } else {
        components.push(new MathRun(' '));
        remaining = remaining.slice(2);
      }
      processed = true;
    }
    if (!processed) {
      match = remaining.match(/^\\([a-zA-Z]+)/);
      if (match) {
        components.push(new MathRun(match[1]));
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      match = remaining.match(/^[^\\^_]+/);
      if (match) {
        components.push(new MathRun(match[0]));
        remaining = remaining.slice(match[0].length);
        processed = true;
      }
    }
    if (!processed) {
      components.push(new MathRun(remaining.charAt(0)));
      remaining = remaining.slice(1);
      processed = true;
    }
  }
  return components.length > 0 ? components : [new MathRun(latex)];
}

// src/services/math/convert-inline-math.ts
function convertInlineMath(node) {
  var _a;
  if (!((_a = node.attrs) == null ? void 0 : _a.latex)) {
    return null;
  }
  const latexString = node.attrs.latex;
  try {
    const mathComponents = parseLatexToMathComponents(latexString);
    return new DocxMath({
      children: mathComponents,
    });
  } catch (_error) {
    return new DocxMath({ children: [new MathRun2(latexString)] });
  }
}

// src/services/text/convert-text-node.ts
import { ExternalHyperlink, HighlightColor, ShadingType, TextRun, UnderlineType } from 'docx';

// src/utils/normalize-color.ts
var CSS_COLOR_MAP = {
  black: '000000',
  blue: '0000FF',
  brown: 'A52A2A',
  cyan: '00FFFF',
  gray: '808080',
  green: '00FF00',
  indigo: '4B0082',
  lime: '00FF00',
  magenta: 'FF00FF',
  maroon: '800000',
  navy: '000080',
  olive: '808000',
  orange: 'FFA500',
  pink: 'FFC0CB',
  purple: '800080',
  red: 'FF0000',
  teal: '008080',
  violet: '9400D3',
  white: 'FFFFFF',
  yellow: 'FFFF00',
};
var UNRESOLVABLE_KEYWORDS = /* @__PURE__ */ new Set([
  'auto',
  'transparent',
  'inherit',
  'initial',
  'unset',
  'currentcolor',
]);
var BARE_6_HEX_RE = /^[0-9A-Fa-f]{6}$/;
function expandShortHex(short) {
  return short[0] + short[0] + short[1] + short[1] + short[2] + short[2];
}
function channelToHex(channel) {
  return Math.max(0, Math.min(255, Math.round(channel)))
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
}
function normalizeColor(color) {
  const trimmed = color.trim();
  if (trimmed === '') {
    return null;
  }
  const lower = trimmed.toLowerCase();
  if (UNRESOLVABLE_KEYWORDS.has(lower)) {
    return null;
  }
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1).toUpperCase();
    if (hex.length === 6 && BARE_6_HEX_RE.test(hex)) {
      return hex;
    }
    if (hex.length === 3 && /^[0-9A-F]{3}$/.test(hex)) {
      return expandShortHex(hex);
    }
    if (hex.length === 8 && /^[0-9A-F]{8}$/.test(hex)) {
      return hex.slice(0, 6);
    }
    if (hex.length === 4 && /^[0-9A-F]{4}$/.test(hex)) {
      return expandShortHex(hex.slice(0, 3));
    }
    return null;
  }
  if (lower.startsWith('rgb')) {
    const match = trimmed.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/);
    if (!match) {
      return null;
    }
    const r = parseFloat(match[1]);
    const g = parseFloat(match[2]);
    const b = parseFloat(match[3]);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return null;
    }
    return channelToHex(r) + channelToHex(g) + channelToHex(b);
  }
  const mapped = CSS_COLOR_MAP[lower];
  if (mapped !== void 0) {
    return mapped;
  }
  if (BARE_6_HEX_RE.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return null;
}

// src/utils/pixels-to-half-points.ts
function pixelsToHalfPoints(pixels) {
  return pixels * 0.75 * 2;
}

// src/services/text/convert-text-node.ts
function sanitizeFontFamily(font) {
  const fonts = font.replace(/"/g, '').split(',');
  const primary = fonts.find((f) => f.trim().length > 0);
  return primary ? primary.trim() : font;
}
function isValidHighlightColor(color) {
  return Object.values(HighlightColor).includes(color);
}
function convertTextNode(node, textRunOverrides) {
  const formatting = {};
  let isLink = false;
  const linkAttrs = {};
  if (node.marks) {
    node.marks.forEach((mark) => {
      var _a, _b;
      switch (mark.type) {
        case 'bold':
          formatting.bold = true;
          break;
        case 'italic':
          formatting.italics = true;
          break;
        case 'underline':
          formatting.underline = {
            type: UnderlineType.SINGLE,
          };
          break;
        case 'strike':
          formatting.strike = true;
          break;
        case 'code':
          formatting.font = 'Courier New';
          formatting.shading = {
            type: 'solid',
            fill: '5805ff',
          };
          formatting.style = 'Code';
          break;
        case 'textStyle':
          if (mark.attrs) {
            if (mark.attrs.color && mark.attrs.color !== '') {
              const normalized = normalizeColor(mark.attrs.color);
              if (normalized !== null) {
                formatting.color = normalized;
              } else {
                console.warn(
                  `[convertTextNode] Unrecognized color: "${mark.attrs.color}". Supported formats: #RRGGBB, #RGB, rgb(r,g,b), rgba(r,g,b,a), and named colors: ${new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(Object.keys(CSS_COLOR_MAP))}. This color will be ignored.`
                );
              }
            }
            if (mark.attrs.fontFamily) {
              formatting.font = sanitizeFontFamily(mark.attrs.fontFamily);
            }
            if (mark.attrs.fontSize) {
              const fontSize = parseInt(mark.attrs.fontSize, 10);
              if (!Number.isNaN(fontSize)) {
                formatting.size = pixelsToHalfPoints(fontSize);
              } else {
                console.warn(
                  `[convertTextNode] Invalid fontSize: ${mark.attrs.fontSize}. Expected a valid number. This value will be ignored.`
                );
              }
            }
            if (
              mark.attrs.backgroundColor &&
              mark.attrs.backgroundColor !== '' &&
              !formatting.shading
            ) {
              const normalized = normalizeColor(mark.attrs.backgroundColor);
              if (normalized !== null) {
                formatting.shading = {
                  type: ShadingType.SOLID,
                  fill: normalized,
                };
              } else {
                console.warn(
                  `[convertTextNode] Unrecognized backgroundColor: "${mark.attrs.backgroundColor}". Supported formats: #RRGGBB, #RGB, rgb(r,g,b), rgba(r,g,b,a), and named colors. This value will be ignored.`
                );
              }
            }
          }
          break;
        case 'highlight':
          if (!((_a = mark.attrs) == null ? void 0 : _a.color)) {
            formatting.highlight = 'yellow';
            break;
          }
          if (isValidHighlightColor(mark.attrs.color)) {
            formatting.highlight = mark.attrs.color;
            break;
          }
          {
            const normalized = normalizeColor(mark.attrs.color);
            if (normalized !== null) {
              formatting.shading = {
                type: ShadingType.SOLID,
                fill: normalized,
              };
            } else {
              formatting.highlight = 'yellow';
            }
          }
          break;
        case 'link':
          isLink = true;
          formatting.style = 'Hyperlink';
          linkAttrs.href = (_b = mark.attrs) == null ? void 0 : _b.href;
          break;
        default:
          break;
      }
    });
  }
  const run = new TextRun({
    ...textRunOverrides,
    text: node.text || '',
    ...formatting,
  });
  if (isLink && linkAttrs) {
    return new ExternalHyperlink({
      link: linkAttrs.href,
      children: [run],
    });
  }
  return run;
}

// src/services/paragraph/convert-paragraph.ts
async function convertParagraph({
  node,
  customNodes,
  options,
  list,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  var _a, _b, _c, _d, _e, _f, _g;
  if (options == null ? void 0 : options.style) {
    const children = (node.content || [])
      .map((child) => {
        if (child.type === 'inlineMath') {
          return convertInlineMath(child);
        }
        if (child.type === 'hardBreak') {
          return new TextRun2({ text: '', break: 1 });
        }
        return convertTextNode(child, textRunOverrides);
      })
      .filter((child) => child !== null);
    return new Paragraph2({
      ...paragraphOverrides,
      children,
      style: options.style,
    });
  }
  let alignment = AlignmentType2.LEFT;
  if ((_a = node.attrs) == null ? void 0 : _a.textAlign) {
    switch (node.attrs.textAlign) {
      case 'center':
        alignment = AlignmentType2.CENTER;
        break;
      case 'right':
        alignment = AlignmentType2.RIGHT;
        break;
      case 'justify':
        alignment = AlignmentType2.JUSTIFIED;
        break;
      default:
        alignment = AlignmentType2.LEFT;
    }
  }
  let lineHeight = 1;
  const textStyleMarks =
    (_c = (_b = node.marks) == null ? void 0 : _b.filter((mark) => mark.type === 'textStyle')) !=
    null
      ? _c
      : [];
  if (
    textStyleMarks.length > 0 &&
    ((_d = textStyleMarks[0].attrs) == null ? void 0 : _d.lineHeight)
  ) {
    lineHeight = textStyleMarks[0].attrs.lineHeight;
  }
  const initial = { paragraphs: [], currentTextRuns: [] };
  const result = await (node.content || []).reduce(async (accPromise, child) => {
    const acc = await accPromise;
    const customNode = customNodes.find((c) => c.type === child.type);
    if (customNode) {
      const childNode = customNode.render(child);
      if (childNode === null) {
        return acc;
      }
      if (childNode instanceof TextRun2) {
        acc.currentTextRuns.push(childNode);
        return acc;
      }
    }
    if (child.type === 'inlineMath') {
      const mathElement = convertInlineMath(child);
      if (mathElement) {
        acc.currentTextRuns.push(mathElement);
      }
      return acc;
    }
    if (child.type === 'hardBreak') {
      acc.currentTextRuns.push(new TextRun2({ text: '', break: 1 }));
      return acc;
    }
    if (child.type === 'image') {
      if (acc.currentTextRuns.length > 0) {
        acc.paragraphs.push(
          new Paragraph2({
            children: [...acc.currentTextRuns],
            alignment,
          })
        );
        acc.currentTextRuns = [];
      }
      const imageParagraph = await convertImage({ node: child, imageOverrides });
      acc.paragraphs.push(imageParagraph);
      return acc;
    }
    const converted = convertTextNode(child, textRunOverrides);
    if (converted) {
      acc.currentTextRuns.push(converted);
    }
    return acc;
  }, Promise.resolve(initial));
  if (result.currentTextRuns.length === 0 && result.paragraphs.length === 0) {
    return [];
  }
  if (result.currentTextRuns.length === 0) {
    return result.paragraphs.length === 1 ? result.paragraphs[0] : result.paragraphs;
  }
  let headingLevel;
  if ((_e = node.attrs) == null ? void 0 : _e.level) {
    switch (node.attrs.level) {
      case 1:
        headingLevel = HeadingLevel.HEADING_1;
        break;
      case 2:
        headingLevel = HeadingLevel.HEADING_2;
        break;
      case 3:
        headingLevel = HeadingLevel.HEADING_3;
        break;
      case 4:
        headingLevel = HeadingLevel.HEADING_4;
        break;
      case 5:
        headingLevel = HeadingLevel.HEADING_5;
        break;
      case 6:
        headingLevel = HeadingLevel.HEADING_6;
        break;
      default:
        headingLevel = void 0;
    }
  }
  result.paragraphs.push(
    new Paragraph2({
      ...paragraphOverrides,
      children: result.currentTextRuns,
      alignment,
      spacing: {
        line: lineHeightToDocx(lineHeight),
      },
      ...((list == null ? void 0 : list.type) === 'bullet' && {
        bullet: { level: (_f = list.level) != null ? _f : 0 },
      }),
      ...((list == null ? void 0 : list.type) === 'ordered' && {
        numbering: {
          reference: 'ordered-list',
          level: (_g = list.level) != null ? _g : 0,
          instance: list.orderedListInstanceId,
        },
      }),
      ...((list == null ? void 0 : list.type) === 'task' && {
        numbering: { reference: 'task-list', level: (_f = list.level) != null ? _f : 0 },
      }),
      ...(headingLevel && { heading: headingLevel }),
    })
  );
  return result.paragraphs.length === 1 ? result.paragraphs[0] : result.paragraphs;
}

// src/services/heading/convert-heading.ts
async function convertHeading({
  node,
  customNodes,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  return convertParagraph({
    node,
    customNodes,
    paragraphOverrides,
    textRunOverrides,
    imageOverrides,
  });
}

// src/services/horizontal-rule/convert-horizontal-rule.ts
import { Paragraph as Paragraph3, Run } from 'docx';

// src/creators/create-horizontal-rule-element.ts
import { BuilderElement as BuilderElement5 } from 'docx';
var createHorizontalRuleElement = () =>
  new BuilderElement5({
    name: 'v:rect',
    attributes: {
      style: {
        key: 'style',
        // width:0.0pt makes the rule to take all of the available space horizontally
        value:
          'width:0.0pt;height:.05pt;mso-width-percent:0;mso-height-percent:0;mso-width-percent:0;mso-height-percent:0',
      },
      hr: {
        key: 'o:hr',
        value: 't',
      },
      hrstd: {
        key: 'o:hrstd',
        value: 't',
      },
      hralign: {
        key: 'o:hralign',
        value: 'center',
      },
      fillcolor: {
        key: 'fillcolor',
        value: '#A0A0A0',
      },
      stroked: {
        key: 'stroked',
        value: 'f',
      },
    },
  });

// src/creators/create-pict-element.ts
import { BuilderElement as BuilderElement6 } from 'docx';
var createPictElement = (children) =>
  new BuilderElement6({
    name: 'w:pict',
    children,
  });

// src/services/horizontal-rule/convert-horizontal-rule.ts
var convertHorizontalRule = () => {
  return new Paragraph3({
    children: [
      new Run({
        children: [createPictElement([createHorizontalRuleElement()])],
      }),
    ],
    style: 'horizontalRule',
  });
};

// src/utils/unique-instance-id.ts
var usedInstanceIds = /* @__PURE__ */ new Set();
function generateUniqueInstanceId() {
  let id;
  do {
    id = Math.floor(Math.random() * 1e6) + 1;
  } while (usedInstanceIds.has(id));
  usedInstanceIds.add(id);
  return id;
}
function cleanupInstanceIds() {
  usedInstanceIds.clear();
}

// src/services/list/convert-ordered-list.ts
async function convertOrderedList({
  node,
  customNodes,
  level = 0,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  const orderedListInstanceId = generateUniqueInstanceId();
  const items = node.content || [];
  const results = await Promise.all(
    items.map((item) =>
      convertListItem({
        item,
        listType: 'ordered',
        level,
        orderedListInstanceId,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      })
    )
  );
  return results.flat().filter((p) => p !== null);
}

// src/services/list/convert-list-item.ts
async function convertListItem({
  item,
  listType,
  level = 0,
  orderedListInstanceId,
  customNodes,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  if (!item.content) {
    return [];
  }
  const result = [];
  const children = item.content;
  for (let i = 0, len = children.length; i < len; i++) {
    const child = children[i];
    if (child.type === 'paragraph') {
      const converted = await convertParagraph({
        node: child,
        customNodes,
        list: {
          type: listType,
          level,
          orderedListInstanceId,
        },
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
      if (Array.isArray(converted)) {
        result.push(...converted);
      } else {
        result.push(converted);
      }
    } else if (child.type === 'bulletList') {
      result.push(
        ...(await convertBulletList({
          node: child,
          customNodes,
          level: level + 1,
          paragraphOverrides,
          textRunOverrides,
          imageOverrides,
        }))
      );
    } else if (child.type === 'orderedList') {
      result.push(
        ...(await convertOrderedList({
          node: child,
          level: level + 1,
          customNodes,
          paragraphOverrides,
          textRunOverrides,
          imageOverrides,
        }))
      );
    } else if (child.type === 'taskList') {
      result.push(
        ...(await convertTaskList({
          node: child,
          customNodes,
          level: level + 1,
          paragraphOverrides,
          textRunOverrides,
          imageOverrides,
        }))
      );
    } else {
      const converted = await convertParagraph({
        node: child,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
      if (Array.isArray(converted)) {
        result.push(...converted);
      } else {
        result.push(converted);
      }
    }
  }
  return result;
}

// src/services/list/convert-bullet-list.ts
async function convertBulletList({
  node,
  customNodes,
  level = 0,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  const items = node.content || [];
  const results = await Promise.all(
    items.map((item) =>
      convertListItem({
        item,
        listType: 'bullet',
        level,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      })
    )
  );
  return results.flat().filter((p) => p !== null);
}

// src/services/list/convert-task-list.ts
async function convertTaskList({
  node,
  customNodes,
  level = 0,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  const items = node.content || [];
  const results = await Promise.all(
    items.map((item) =>
      convertListItem({
        item,
        listType: 'task',
        level,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      })
    )
  );
  return results.flat().filter((p) => p !== null);
}

// src/services/math/convert-block-math.ts
import { Math as DocxMath2, MathRun as MathRun3, Paragraph as Paragraph4 } from 'docx';
function convertBlockMath({ node }) {
  var _a;
  if (!((_a = node.attrs) == null ? void 0 : _a.latex)) {
    return null;
  }
  const latexString = node.attrs.latex;
  try {
    const mathComponents = parseLatexToMathComponents(latexString);
    return new Paragraph4({
      children: [new DocxMath2({ children: mathComponents })],
    });
  } catch (_error) {
    return new Paragraph4({ children: [new DocxMath2({ children: [new MathRun3(latexString)] })] });
  }
}

// src/services/quote/convert-quote.ts
async function convertQuote({
  node,
  customNodes,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  if (!node.content) {
    return [];
  }
  const paragraphsResults = await Promise.all(
    node.content.map((child) =>
      convertParagraph({
        node: child,
        customNodes,
        options: { style: 'Quote' },
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      })
    )
  );
  return paragraphsResults.flatMap((p) => (Array.isArray(p) ? p : [p]));
}

// src/services/table/convert-table.ts
import {
  BorderStyle,
  convertInchesToTwip,
  Table as Table2,
  TableLayoutType,
  TableRow,
  WidthType as WidthType2,
} from 'docx';

// src/services/table/convert-table-cell.ts
import { Paragraph as Paragraph5, Table, TableCell, WidthType } from 'docx';
async function convertTableCell({
  node,
  customNodes,
  columnWidthPercentage,
  columnWidthTwips,
  totalColumns,
  pageSize,
  pageMargins,
  tableOverrides,
  tableCellOverrides,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  var _a, _b, _c;
  const isHeaderCell = node.type === 'tableHeader';
  const cellTextRunOverrides = isHeaderCell
    ? { ...textRunOverrides, bold: true, color: '0F172A' }
    : textRunOverrides;
  const childPromises = (node.content || []).map((child) =>
    convertNode({
      node: child,
      customNodes,
      pageSize,
      pageMargins,
      tableOverrides,
      tableCellOverrides,
      paragraphOverrides,
      textRunOverrides: cellTextRunOverrides,
      imageOverrides,
    })
  );
  const convertedResults = await Promise.all(childPromises);
  const children = [];
  for (const result of convertedResults) {
    if (Array.isArray(result)) {
      children.push(...result);
    } else if (result instanceof Paragraph5 || result instanceof Table) {
      children.push(result);
    }
  }
  if (children.length === 0) {
    children.push(new Paragraph5({ children: [] }));
  }
  const colspan = ((_a = node.attrs) == null ? void 0 : _a.colspan)
    ? Number(node.attrs.colspan)
    : 1;
  const colwidth = (_c = (_b = node.attrs) == null ? void 0 : _b.colwidth) != null ? _c : null;
  const colwidthTwips =
    colwidth && colwidth.length > 0
      ? colwidth.reduce((sum, px) => sum + Math.round(px * 15), 0)
      : null;
  const usesTwips = columnWidthTwips !== void 0;
  let cellWidth;
  if (colwidthTwips !== null) {
    cellWidth = colwidthTwips;
  } else if (usesTwips) {
    cellWidth = columnWidthTwips * colspan;
  } else if (columnWidthPercentage) {
    cellWidth = columnWidthPercentage * colspan;
  } else {
    cellWidth = (100 / (totalColumns || 1)) * colspan;
  }
  const cellOptions = {
    ...tableCellOverrides,
    children,
    width: {
      size: cellWidth,
      type: colwidthTwips !== null || usesTwips ? WidthType.DXA : WidthType.PERCENTAGE,
    },
    ...(isHeaderCell && {
      shading: { type: ShadingType.SOLID, color: 'auto', fill: 'F1F5F9' },
    }),
  };
  if (node.attrs) {
    if (node.attrs.colspan && Number(node.attrs.colspan) > 1) {
      cellOptions.columnSpan = Number(node.attrs.colspan);
    }
    if (node.attrs.rowspan && Number(node.attrs.rowspan) > 1) {
      cellOptions.rowSpan = Number(node.attrs.rowspan);
    }
  }
  return new TableCell(cellOptions);
}

// src/services/table/convert-table.ts
function calculateTableColumns(tableNode) {
  const rowColumnCounts = (tableNode.content || []).map((row) => {
    return (row.content || []).reduce((rowColumns, cell) => {
      var _a;
      const colspan = ((_a = cell.attrs) == null ? void 0 : _a.colspan)
        ? Number(cell.attrs.colspan)
        : 1;
      return rowColumns + Math.max(1, colspan);
    }, 0);
  });
  const maxColumns = Math.max(...rowColumnCounts, 0);
  return maxColumns || 1;
}
async function convertTable({
  node,
  customNodes,
  pageSize,
  pageMargins,
  tableOverrides,
  tableCellOverrides,
  paragraphOverrides,
  textRunOverrides,
  imageOverrides,
}) {
  const totalColumns = calculateTableColumns(node);
  const pageWidth = (pageSize == null ? void 0 : pageSize.width) || '21.0cm';
  const leftMargin = (pageMargins == null ? void 0 : pageMargins.left) || '3.17cm';
  const rightMargin = (pageMargins == null ? void 0 : pageMargins.right) || '3.17cm';
  const parseAndConvertToTwips = (value) => {
    var _a;
    const unit = ((_a = value.match(/[a-z%]+$/i)) == null ? void 0 : _a[0]) || 'cm';
    const numValue = parseFloat(value.replace(/[a-z%]/gi, ''));
    switch (unit.toLowerCase()) {
      case 'cm':
        return Math.round(numValue * 567);
      // 1 cm = 567 twips
      case 'in':
        return Math.round(numValue * 1440);
      // 1 inch = 1440 twips
      case 'pt':
        return Math.round(numValue * 20);
      // 1 point = 20 twips
      case 'pc':
        return Math.round(numValue * 240);
      // 1 pica = 12 points = 240 twips
      case 'mm':
        return Math.round(numValue * 56.7);
      // 1 mm = 56.7 twips
      case 'px':
        return Math.round(numValue * 15);
      // 1 pixel ≈ 15 twips (assuming 96 DPI)
      default:
        return Math.round(numValue * 567);
    }
  };
  const pageWidthTwips = parseAndConvertToTwips(pageWidth);
  const leftMarginTwips = parseAndConvertToTwips(leftMargin);
  const rightMarginTwips = parseAndConvertToTwips(rightMargin);
  const availableWidthTwips = pageWidthTwips - leftMarginTwips - rightMarginTwips;
  const columnWidthPercentage = 100 / totalColumns;
  const columnWidthTwips = Math.floor(availableWidthTwips / totalColumns);
  const rows = await Promise.all(
    (node.content || []).map(async (row) => {
      const cells = await Promise.all(
        (row.content || []).map(async (cell) => {
          return convertTableCell({
            node: cell,
            customNodes,
            columnWidthPercentage,
            columnWidthTwips,
            totalColumns,
            pageSize,
            pageMargins,
            tableOverrides,
            tableCellOverrides,
            paragraphOverrides,
            textRunOverrides,
            imageOverrides,
          });
        })
      );
      return new TableRow({ children: cells });
    })
  );
  const defaultBorder = { style: BorderStyle.SINGLE, size: 4, color: 'auto' };
  return new Table2({
    width: {
      size: availableWidthTwips,
      type: WidthType2.DXA,
      // Use DXA (twips) for precise width control
    },
    layout: TableLayoutType.FIXED,
    columnWidths: Array(totalColumns).fill(columnWidthTwips),
    margins: {
      top: convertInchesToTwip(0.08),
      bottom: convertInchesToTwip(0.08),
      left: convertInchesToTwip(0.08),
      right: convertInchesToTwip(0.08),
    },
    borders: {
      top: defaultBorder,
      bottom: defaultBorder,
      left: defaultBorder,
      right: defaultBorder,
      insideHorizontal: defaultBorder,
      insideVertical: defaultBorder,
    },
    ...tableOverrides,
    rows,
    // rows must come last to prevent runtime overrides
  });
}

// src/services/node/convert-node.ts
async function convertNode({
  node,
  customNodes = [],
  pageSize,
  pageMargins,
  tableOverrides,
  paragraphOverrides,
  textRunOverrides,
  tableCellOverrides,
  imageOverrides,
}) {
  switch (node.type) {
    case 'paragraph':
      return convertParagraph({
        node,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'bulletList':
      return convertBulletList({
        node,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'orderedList':
      return convertOrderedList({
        node,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'taskList':
      return convertTaskList({
        node,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'taskItem':
      return convertListItem({
        item: node,
        listType: 'task',
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'listItem':
      return convertListItem({
        item: node,
        listType: 'bullet',
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'table':
      return convertTable({
        node,
        customNodes,
        pageSize,
        pageMargins,
        tableOverrides,
        tableCellOverrides,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'image':
      return [await convertImage({ node, imageOverrides })];
    case 'heading':
      return convertHeading({
        node,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'blockquote':
      return convertQuote({
        node,
        customNodes,
        paragraphOverrides,
        textRunOverrides,
        imageOverrides,
      });
    case 'horizontalRule':
      return convertHorizontalRule();
    case 'blockMath':
      return convertBlockMath({ node });
    default: {
      if (customNodes) {
        const customNode = customNodes.find((c) => c.type === node.type);
        if (!customNode) {
          console.error(`Custom node "${node.type}" not found`);
          return [];
        }
        return customNode.render(node);
      }
      return [];
    }
  }
}

// src/utils/deep-merge.ts
function deepMerge(target, source) {
  if (typeof target !== 'object' || target === null) {
    return source;
  }
  if (typeof source !== 'object' || source === null) {
    return target;
  }
  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = target[key];
    if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
      const mergedArray = [...targetValue];
      sourceValue.forEach((sourceItem) => {
        if (sourceItem && typeof sourceItem === 'object' && sourceItem.id) {
          const index = mergedArray.findIndex((item) => item.id === sourceItem.id);
          if (index !== -1) {
            mergedArray[index] = deepMerge(mergedArray[index], sourceItem);
          } else {
            mergedArray.push(sourceItem);
          }
        } else {
          mergedArray.push(sourceItem);
        }
      });
      target[key] = mergedArray;
    } else if (typeof sourceValue === 'object') {
      target[key] = deepMerge(
        typeof targetValue === 'object' && targetValue !== null ? targetValue : {},
        sourceValue
      );
    } else {
      target[key] = sourceValue;
    }
  });
  return target;
}

// src/convert.ts
async function convert({
  node,
  customNodes = [],
  styleOverrides,
  pageSize,
  pageMargins,
  tableOverrides,
  paragraphOverrides,
  textRunOverrides,
  tableCellOverrides,
  imageOverrides,
  headers,
  footers,
}) {
  const styleOverridesMerged = deepMerge(defaultStyles, styleOverrides);
  const childrenPromises = (node.content || []).map((child) =>
    convertNode({
      node: child,
      customNodes,
      styleOverrides: styleOverridesMerged,
      pageSize,
      pageMargins,
      tableOverrides,
      paragraphOverrides,
      textRunOverrides,
      tableCellOverrides,
      imageOverrides,
    })
  );
  const convertedResults = await Promise.all(childrenPromises);
  const children = convertedResults.flatMap((result) => {
    if (Array.isArray(result)) {
      return result;
    }
    if (result instanceof Paragraph6 || result instanceof Table3) {
      return [result];
    }
    return [];
  });
  const headersConverted = {
    default:
      (headers == null ? void 0 : headers.default) instanceof Function
        ? await headers.default()
        : headers == null
          ? void 0
          : headers.default,
    first:
      (headers == null ? void 0 : headers.first) instanceof Function
        ? await headers.first()
        : headers == null
          ? void 0
          : headers.first,
    even:
      (headers == null ? void 0 : headers.even) instanceof Function
        ? await headers.even()
        : headers == null
          ? void 0
          : headers.even,
  };
  const footersConverted = {
    default:
      (footers == null ? void 0 : footers.default) instanceof Function
        ? await footers.default()
        : footers == null
          ? void 0
          : footers.default,
    first:
      (footers == null ? void 0 : footers.first) instanceof Function
        ? await footers.first()
        : footers == null
          ? void 0
          : footers.first,
    even:
      (footers == null ? void 0 : footers.even) instanceof Function
        ? await footers.even()
        : footers == null
          ? void 0
          : footers.even,
  };
  cleanupInstanceIds();
  return new Document({
    numbering: {
      config: [
        {
          reference: 'ordered-list',
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 360 },
                },
              },
            },
            {
              level: 1,
              format: LevelFormat.DECIMAL,
              text: '%2.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 1140, hanging: 360 },
                },
              },
            },
            {
              level: 2,
              format: LevelFormat.DECIMAL,
              text: '%3.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 1440, hanging: 360 },
                },
              },
            },
            {
              level: 3,
              format: LevelFormat.DECIMAL,
              text: '%4.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 1740, hanging: 360 },
                },
              },
            },
            {
              level: 4,
              format: LevelFormat.DECIMAL,
              text: '%5.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 2040, hanging: 360 },
                },
              },
            },
            {
              level: 5,
              format: LevelFormat.DECIMAL,
              text: '%6.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 2340, hanging: 360 },
                },
              },
            },
            {
              level: 6,
              format: LevelFormat.DECIMAL,
              text: '%7.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 2640, hanging: 360 },
                },
              },
            },
            {
              level: 7,
              format: LevelFormat.DECIMAL,
              text: '%8.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 2940, hanging: 360 },
                },
              },
            },
            {
              level: 8,
              format: LevelFormat.DECIMAL,
              text: '%9.',
              alignment: 'left',
              style: {
                paragraph: {
                  indent: { left: 3240, hanging: 360 },
                },
              },
            },
          ],
        },
        {
          reference: 'task-list',
          levels: Array.from({ length: 9 }, (_, i) => ({
            level: i,
            format: LevelFormat.BULLET,
            text: '\u2610',
            alignment: 'left',
            style: {
              paragraph: {
                indent: { left: 720 + i * 360, hanging: 360 },
              },
            },
          })),
        },
      ],
    },
    evenAndOddHeaderAndFooters:
      !!(headers == null ? void 0 : headers.evenAndOddHeaders) ||
      !!(footers == null ? void 0 : footers.evenAndOddFooters),
    sections: [
      {
        properties: {
          page: {
            size: {
              width: (pageSize == null ? void 0 : pageSize.width) || '21.0cm',
              height: (pageSize == null ? void 0 : pageSize.height) || '29.7cm',
            },
            margin: {
              top: (pageMargins == null ? void 0 : pageMargins.top) || '2.54cm',
              bottom: (pageMargins == null ? void 0 : pageMargins.bottom) || '2.54cm',
              left: (pageMargins == null ? void 0 : pageMargins.left) || '3.17cm',
              right: (pageMargins == null ? void 0 : pageMargins.right) || '3.17cm',
            },
          },
          titlePage:
            !!(headers == null ? void 0 : headers.differentFirstPage) ||
            !!(footers == null ? void 0 : footers.differentFirstPage),
        },
        headers: headersConverted,
        footers: footersConverted,
        children,
      },
    ],
    styles: styleOverridesMerged,
  });
}

// src/export-docx.ts
var exportDocx = async ({
  document,
  exportType,
  customNodes,
  styleOverrides,
  pageSize,
  pageMargins,
  tableOverrides,
  paragraphOverrides,
  textRunOverrides,
  tableCellOverrides,
  imageOverrides,
  headers = void 0,
  footers = void 0,
}) => {
  const doc = await convert({
    node: document,
    styleOverrides,
    customNodes,
    headers,
    footers,
    pageSize,
    pageMargins,
    tableOverrides,
    paragraphOverrides,
    textRunOverrides,
    tableCellOverrides,
    imageOverrides,
  });
  if (!doc) {
    throw new Error('Failed to convert document');
  }
  let packerResult;
  try {
    switch (exportType) {
      case 'buffer':
        packerResult = await Packer.toBuffer(doc);
        break;
      case 'string':
        packerResult = await Packer.toString(doc);
        break;
      case 'base64':
        packerResult = await Packer.toBase64String(doc);
        break;
      case 'blob':
        packerResult = await Packer.toBlob(doc);
        break;
      case 'stream':
        packerResult = Packer.toStream(doc);
        break;
      default:
        throw new Error('Invalid export type');
    }
  } catch (error) {
    throw new Error(`Failed to export document: ${error.message}`);
  }
  return packerResult;
};

// src/extension.ts
import { Extension } from '@tiptap/core';
function getPagesStorage(storage) {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const pagesStorage = Reflect.get(storage, 'pages');
  if (!pagesStorage || typeof pagesStorage !== 'object') {
    return null;
  }
  const differentOddEven = Reflect.get(pagesStorage, 'differentOddEven');
  const differentFirstPage = Reflect.get(pagesStorage, 'differentFirstPage');
  if (typeof differentOddEven !== 'boolean' || typeof differentFirstPage !== 'boolean') {
    return null;
  }
  return {
    headerJSON: (_a = Reflect.get(pagesStorage, 'headerJSON')) != null ? _a : null,
    headerEvenJSON: (_b = Reflect.get(pagesStorage, 'headerEvenJSON')) != null ? _b : null,
    headerOddJSON: (_c = Reflect.get(pagesStorage, 'headerOddJSON')) != null ? _c : null,
    headerFirstPageJSON:
      (_d = Reflect.get(pagesStorage, 'headerFirstPageJSON')) != null ? _d : null,
    footerJSON: (_e = Reflect.get(pagesStorage, 'footerJSON')) != null ? _e : null,
    footerEvenJSON: (_f = Reflect.get(pagesStorage, 'footerEvenJSON')) != null ? _f : null,
    footerOddJSON: (_g = Reflect.get(pagesStorage, 'footerOddJSON')) != null ? _g : null,
    footerFirstPageJSON:
      (_h = Reflect.get(pagesStorage, 'footerFirstPageJSON')) != null ? _h : null,
    differentOddEven,
    differentFirstPage,
  };
}
var ExportDocx = Extension.create({
  name: 'export-docx',
  addOptions() {
    return {
      styleOverrides: {},
      customNodes: [],
      exportType: 'blob',
      headers: void 0,
      footers: void 0,
      pageSize: void 0,
      pageMargins: void 0,
      tableOverrides: void 0,
      paragraphOverrides: void 0,
      textRunOverrides: void 0,
      tableCellOverrides: void 0,
      imageOverrides: void 0,
      onCompleteExport: () => {
        throw new Error(
          'The [exportDocx] extension requires an [onCompleteExport] callback function to handle the exported DOCX file'
        );
      },
    };
  },
  // @ts-expect-error: asynchronous handling of functions
  // is not well supported by the current type
  // definitions. We would need to think of
  // a better way to handle async commands.
  addCommands() {
    return {
      exportDocx:
        (options) =>
        async ({ editor }) => {
          var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
          const pagesExtension = editor.extensionManager.extensions.find(
            (ext) => ext.name === 'pages'
          );
          const pagesStorage = pagesExtension ? getPagesStorage(editor.storage) : null;
          const pmDoc = editor.getJSON();
          const pagesHeader = pagesStorage ? pagesStorage.headerJSON : null;
          const pagesEvenHeader = pagesStorage ? pagesStorage.headerEvenJSON : null;
          const pagesOddHeader = pagesStorage ? pagesStorage.headerOddJSON : null;
          const pagesFirstHeader = pagesStorage ? pagesStorage.headerFirstPageJSON : null;
          const pagesFooter = pagesStorage ? pagesStorage.footerJSON : null;
          const pagesEvenFooter = pagesStorage ? pagesStorage.footerEvenJSON : null;
          const pagesOddFooter = pagesStorage ? pagesStorage.footerOddJSON : null;
          const pagesFirstFooter = pagesStorage ? pagesStorage.footerFirstPageJSON : null;
          const exportOptions = {
            document: pmDoc,
            styleOverrides:
              (_a = options == null ? void 0 : options.styleOverrides) != null
                ? _a
                : this.options.styleOverrides,
            customNodes:
              (_b = options == null ? void 0 : options.customNodes) != null
                ? _b
                : this.options.customNodes,
            exportType:
              (_c = options == null ? void 0 : options.exportType) != null
                ? _c
                : this.options.exportType,
            pageSize:
              (_d = options == null ? void 0 : options.pageSize) != null
                ? _d
                : this.options.pageSize,
            pageMargins:
              (_e = options == null ? void 0 : options.pageMargins) != null
                ? _e
                : this.options.pageMargins,
            tableOverrides:
              (_f = options == null ? void 0 : options.tableOverrides) != null
                ? _f
                : this.options.tableOverrides,
            paragraphOverrides:
              (_g = options == null ? void 0 : options.paragraphOverrides) != null
                ? _g
                : this.options.paragraphOverrides,
            textRunOverrides:
              (_h = options == null ? void 0 : options.textRunOverrides) != null
                ? _h
                : this.options.textRunOverrides,
            tableCellOverrides:
              (_i = options == null ? void 0 : options.tableCellOverrides) != null
                ? _i
                : this.options.tableCellOverrides,
            imageOverrides:
              (_j = options == null ? void 0 : options.imageOverrides) != null
                ? _j
                : this.options.imageOverrides,
          };
          if (!(options == null ? void 0 : options.headers) && pagesStorage) {
            exportOptions.headers = {};
            exportOptions.headers.default = () =>
              // Docx.js uses default header as odd header when differentOddEven is true
              convertHeader({
                node: pagesStorage.differentOddEven ? pagesOddHeader : pagesHeader,
              });
            exportOptions.headers.evenAndOddHeaders = pagesStorage.differentOddEven;
            exportOptions.headers.differentFirstPage = pagesStorage.differentFirstPage;
            exportOptions.headers.even = pagesStorage.differentOddEven
              ? () => convertHeader({ node: pagesEvenHeader })
              : void 0;
            exportOptions.headers.first = pagesStorage.differentFirstPage
              ? () => convertHeader({ node: pagesFirstHeader })
              : void 0;
          } else {
            exportOptions.headers =
              (_k = options == null ? void 0 : options.headers) != null ? _k : this.options.headers;
          }
          if (!(options == null ? void 0 : options.footers) && pagesStorage) {
            exportOptions.footers = {};
            exportOptions.footers.default = () =>
              // Docx.js uses default footer as odd footer when differentOddEven is true
              convertFooter({
                node: pagesStorage.differentOddEven ? pagesOddFooter : pagesFooter,
              });
            exportOptions.footers.evenAndOddFooters = pagesStorage.differentOddEven;
            exportOptions.footers.differentFirstPage = pagesStorage.differentFirstPage;
            exportOptions.footers.even = pagesStorage.differentOddEven
              ? () => convertFooter({ node: pagesEvenFooter })
              : void 0;
            exportOptions.footers.first = pagesStorage.differentFirstPage
              ? () => convertFooter({ node: pagesFirstFooter })
              : void 0;
          } else {
            exportOptions.footers =
              (_l = options == null ? void 0 : options.footers) != null ? _l : this.options.footers;
          }
          const doc = await exportDocx(exportOptions);
          if (options == null ? void 0 : options.onCompleteExport) {
            options.onCompleteExport(doc);
          } else {
            this.options.onCompleteExport(doc);
          }
          return true;
        },
    };
  },
});

// src/services/footer/convert-footer.ts
import { Footer } from 'docx';
async function convertFooter({ node }) {
  var _a;
  if (!node) {
    return new Footer({ children: [] });
  }
  const withoutDocNode =
    node.type === 'doc' ? ((_a = node.content) == null ? void 0 : _a[0]) : node;
  if (!withoutDocNode) {
    console.warn('Footer node must have a content');
    return new Footer({ children: [] });
  }
  if (withoutDocNode.type !== 'paragraph' && withoutDocNode.type !== 'table') {
    console.warn('Footer node must be a paragraph or table');
    return new Footer({ children: [] });
  }
  const convertedNode = await convertNode({ node: withoutDocNode, customNodes: [] });
  if (!convertedNode) {
    console.warn('Failed to convert Footer node');
    return new Footer({ children: [] });
  }
  if (Array.isArray(convertedNode)) {
    return new Footer({ children: convertedNode });
  }
  return new Footer({ children: [convertedNode] });
}

// src/services/header/convert-header.ts
import { Header } from 'docx';
async function convertHeader({ node }) {
  var _a;
  if (!node) {
    return new Header({ children: [] });
  }
  const withoutDocNode =
    node.type === 'doc' ? ((_a = node.content) == null ? void 0 : _a[0]) : node;
  if (!withoutDocNode) {
    console.warn('Header node must have a content');
    return new Header({ children: [] });
  }
  if (withoutDocNode.type !== 'paragraph' && withoutDocNode.type !== 'table') {
    console.warn('Header node must be a paragraph or table');
    return new Header({ children: [] });
  }
  const convertedNode = await convertNode({ node: withoutDocNode, customNodes: [] });
  if (!convertedNode) {
    console.warn('Failed to convert Header node');
    return new Header({ children: [] });
  }
  if (Array.isArray(convertedNode)) {
    return new Header({ children: convertedNode });
  }
  return new Header({ children: [convertedNode] });
}

// src/services/table/convert-table-row.ts
import { TableRow as TableRow2 } from 'docx';
async function convertTableRow({ node, customNodes }) {
  const cellsResults = await Promise.all(
    (node.content || []).map((cellNode) =>
      convertTableCell({
        node: cellNode,
        customNodes,
        // Use backward compatible defaults when not provided
        columnWidthPercentage: void 0,
        columnWidthTwips: void 0,
        totalColumns: void 0,
      })
    )
  );
  const cells = cellsResults.filter((c) => c !== null);
  return new TableRow2({
    children: cells,
  });
}

// src/utils/pixels-to-points.ts
function pixelsToPoints2(pixels) {
  return pixels * 0.75;
}
export {
  Docx,
  ExportDocx,
  convertBlockMath,
  convertBulletList,
  convertFooter,
  convertHeader,
  convertHeading,
  convertHorizontalRule,
  convertImage,
  convertInlineMath,
  convertListItem,
  convertNode,
  convertOrderedList,
  convertParagraph,
  convertQuote,
  convertTable,
  convertTableCell,
  convertTableRow,
  convertTextNode,
  exportDocx,
  lineHeightToDocx,
  pixelsToHalfPoints,
  pixelsToPoints2 as pixelsToPoints,
  pointsToHalfPoints,
  pointsToTwips,
};
